import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trophy, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const AuthPage = () => {
  const { API, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [rememberMe, setRememberMe] = useState(true);

  const [registerData, setRegisterData] = useState({
    full_name: '',
    email: '',
    phone: '',
    skill_level: '',
    city: '',
    birth_year: '',
    playing_hand: '',
    years_playing: '',
    favorite_surface: '',
    password: '',
    confirmPassword: ''
  });

  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [resetData, setResetData] = useState({
    email: '',
    reset_code: '',
    new_password: '',
    confirmPassword: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      login(response.data.access_token, response.data.user, rememberMe);
      navigate('/');
    } catch (error) {
      console.error('Login error:', error.response?.data);
      
      // Handle different error formats
      let errorMessage = 'שגיאה בהתחברות';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle Pydantic validation errors (array of errors)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            const field = err.loc?.[err.loc.length - 1] || 'שדה';
            const msg = err.msg || 'שגיאה בולידציה';
            return `${field}: ${msg}`;
          }).join(', ');
        } 
        // Handle string error messages
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Handle object error messages
        else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (registerData.password !== registerData.confirmPassword) {
      toast.error('הסיסמאות אינן זהות');
      return;
    }

    if (!registerData.skill_level) {
      toast.error('אנא בחר רמת משחק');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...dataToSend } = registerData;
      const response = await axios.post(`${API}/auth/register`, dataToSend);
      login(response.data.access_token, response.data.user);
      toast.success('נרשמת בהצלחה! נשלח אליך מייל עם פרטי החשבון');
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error.response?.data);
      
      // Handle different error formats
      let errorMessage = 'שגיאה בהרשמה';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle Pydantic validation errors (array of errors)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            // Extract field name and message
            const field = err.loc?.[err.loc.length - 1] || 'שדה';
            const msg = err.msg || 'שגיאה בולידציה';
            return `${field}: ${msg}`;
          }).join(', ');
        } 
        // Handle string error messages
        else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
        // Handle object error messages
        else if (errorData.detail && typeof errorData.detail === 'object') {
          errorMessage = JSON.stringify(errorData.detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
      
      // Show success message
      toast.success('נשלח קוד איפוס לאימייל שלך!');
      
      // If in dev mode, show the code
      if (response.data.dev_code) {
        toast.info(`קוד איפוס (Dev): ${response.data.dev_code}`, { duration: 10000 });
        setResetData({ ...resetData, email: forgotPasswordEmail, reset_code: response.data.dev_code });
      } else {
        setResetData({ ...resetData, email: forgotPasswordEmail });
      }
      
      setIsForgotPassword(false);
      setIsResetPassword(true);
    } catch (error) {
      console.error('Forgot password error:', error.response?.data);
      
      let errorMessage = 'שגיאה בשליחת קוד איפוס';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            const field = err.loc?.[err.loc.length - 1] || 'שדה';
            const msg = err.msg || 'שגיאה בולידציה';
            return `${field}: ${msg}`;
          }).join(', ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (resetData.new_password !== resetData.confirmPassword) {
      toast.error('הסיסמאות אינן זהות');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetData.email,
        reset_code: resetData.reset_code,
        new_password: resetData.new_password
      });
      toast.success('הסיסמא שונתה בהצלחה!');
      setIsResetPassword(false);
      setIsLogin(true);
      setResetData({ email: '', reset_code: '', new_password: '', confirmPassword: '' });
    } catch (error) {
      console.error('Reset password error:', error.response?.data);
      
      let errorMessage = 'שגיאה באיפוס הסיסמא';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(err => {
            const field = err.loc?.[err.loc.length - 1] || 'שדה';
            const msg = err.msg || 'שגיאה בולידציה';
            return `${field}: ${msg}`;
          }).join(', ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex flex-col">
      <div className="p-6">
        <Link to="/" className="flex items-center gap-3 w-fit" data-testid="auth-home-link">
          <img 
            src="/assets/tennis-pro-logo.jpg" 
            alt="Tennis Pro Israel Logo" 
            className="w-10 h-10 object-contain rounded-lg shadow-sm"
          />
          <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
            Tennis Pro Israel
          </span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md glass-effect" data-testid="auth-card">
          <CardHeader className="text-center">
            {/* Logo in Card */}
            <div className="flex justify-center mb-4">
              <img 
                src="/assets/tennis-pro-logo.jpg" 
                alt="Tennis Pro Israel Logo" 
                className="w-16 h-16 object-contain rounded-lg shadow-md"
              />
            </div>
            <CardTitle className="text-3xl font-bold">
              {isForgotPassword ? 'שכחתי סיסמא' : isResetPassword ? 'איפוס סיסמא' : isLogin ? 'התחברות' : 'הרשמה'}
            </CardTitle>
            <CardDescription className="text-base">
              {isForgotPassword ? 'נשלח לך קוד איפוס לאימייל' : 
               isResetPassword ? 'הזן את קוד האיפוס והסיסמא החדשה' :
               isLogin ? 'היכנס לחשבון שלך' : 'הצטרף לקהילת הטניס'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isForgotPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-password-form">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">אימייל</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="email@example.com"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'שולח...' : 'שלח קוד איפוס'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setIsForgotPassword(false);
                    setIsLogin(true);
                  }}
                >
                  חזור להתחברות
                </Button>
              </form>
            ) : isResetPassword ? (
              <form onSubmit={handleResetPassword} className="space-y-4" data-testid="reset-password-form">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    📧 קוד האיפוס נשלח לאימייל <strong>{resetData.email}</strong>
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    💡 במצב פיתוח: הקוד מוצג גם בהודעה למעלה
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-code">קוד איפוס (6 תווים)</Label>
                  <Input
                    id="reset-code"
                    type="text"
                    placeholder="ABC123"
                    value={resetData.reset_code}
                    onChange={(e) => setResetData({...resetData, reset_code: e.target.value.toUpperCase()})}
                    required
                    data-testid="reset-code-input"
                    className="text-center text-2xl tracking-widest uppercase font-bold"
                    maxLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">סיסמא חדשה</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="********"
                    value={resetData.new_password}
                    onChange={(e) => setResetData({...resetData, new_password: e.target.value})}
                    required
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password">אימות סיסמא</Label>
                  <Input
                    id="confirm-new-password"
                    type="password"
                    placeholder="********"
                    value={resetData.confirmPassword}
                    onChange={(e) => setResetData({...resetData, confirmPassword: e.target.value})}
                    required
                    data-testid="confirm-new-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'מאפס...' : 'אפס סיסמא'}
                </Button>
              </form>
            ) : isLogin ? (
              <>
                {/* Google Sign-In Button */}
                {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                <button
                  type="button"
                  onClick={() => {
                    const redirectUrl = window.location.origin + '/auth/callback';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  data-testid="google-signin-btn"
                  className="w-full mb-4 flex items-center justify-center gap-3 px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-800 font-semibold hover:border-blue-400 hover:shadow-md transition-all"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>התחבר/י עם Google</span>
                </button>

                {/* Divider */}
                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-3 text-slate-500 font-bold">או עם אימייל</span>
                  </div>
                </div>

                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-email">אימייל</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="email@example.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">סיסמא</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="********"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    required
                    data-testid="login-password-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="remember-me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    data-testid="remember-me-checkbox"
                  />
                  <Label htmlFor="remember-me" className="text-sm font-normal cursor-pointer">
                    זכור אותי (הישאר מחובר)
                  </Label>
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? 'מתחבר...' : 'היכנס'}
                  <ArrowRight className="mr-2 w-5 h-5" />
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(false);
                    setIsForgotPassword(true);
                  }}
                  className="w-full text-center text-blue-600 hover:text-blue-700 text-sm font-medium"
                  data-testid="forgot-password-link"
                >
                  שכחתי סיסמא
                </button>
              </form>
              </>
            ) : (
              <form onSubmit={handleRegister} className="space-y-3 max-h-[60vh] overflow-y-auto px-1" data-testid="register-form">
                <div className="space-y-2">
                  <Label htmlFor="register-name">שם מלא *</Label>
                  <Input
                    id="register-name"
                    type="text"
                    placeholder="שם מלא"
                    value={registerData.full_name}
                    onChange={(e) => setRegisterData({...registerData, full_name: e.target.value})}
                    required
                    data-testid="register-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-email">אימייל *</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="email@example.com"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                    required
                    data-testid="register-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-phone">טלפון *</Label>
                  <Input
                    id="register-phone"
                    type="tel"
                    placeholder="054-1234567"
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({...registerData, phone: e.target.value})}
                    required
                    data-testid="register-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-level">רמת משחק *</Label>
                  <Select
                    value={registerData.skill_level}
                    onValueChange={(value) => setRegisterData({...registerData, skill_level: value})}
                  >
                    <SelectTrigger id="register-level" data-testid="register-level-select">
                      <SelectValue placeholder="בחר רמה" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="דרג א">דרג א (רמה 4-5)</SelectItem>
                      <SelectItem value="דרג ב">דרג ב (רמה 5-6)</SelectItem>
                      <SelectItem value="דרג ג">דרג ג (רמה 6-7)</SelectItem>
                      <SelectItem value="יחידים open">יחידים Open (רמה 3-4)</SelectItem>
                      <SelectItem value="זוגות open">זוגות Open (כל הרמות)</SelectItem>
                      <SelectItem value="נשים">נשים (רמה 5-7)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-2 pb-2 border-t">
                  <p className="text-sm text-gray-600 mb-3">פרטים נוספים (אופציונלי)</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-city">עיר</Label>
                  <Input
                    id="register-city"
                    type="text"
                    placeholder="תל אביב"
                    value={registerData.city}
                    onChange={(e) => setRegisterData({...registerData, city: e.target.value})}
                    data-testid="register-city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-birth-year">שנת לידה</Label>
                  <Input
                    id="register-birth-year"
                    type="number"
                    placeholder="1990"
                    min="1940"
                    max={new Date().getFullYear()}
                    value={registerData.birth_year}
                    onChange={(e) => setRegisterData({...registerData, birth_year: e.target.value})}
                    data-testid="register-birth-year-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-hand">יד משחק</Label>
                  <Select
                    value={registerData.playing_hand}
                    onValueChange={(value) => setRegisterData({...registerData, playing_hand: value})}
                  >
                    <SelectTrigger id="register-hand">
                      <SelectValue placeholder="בחר יד" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ימין">ימין</SelectItem>
                      <SelectItem value="שמאל">שמאל</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-years">כמה שנים משחק/ת טניס</Label>
                  <Input
                    id="register-years"
                    type="number"
                    placeholder="5"
                    min="0"
                    max="80"
                    value={registerData.years_playing}
                    onChange={(e) => setRegisterData({...registerData, years_playing: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="register-surface">משטח אהוב</Label>
                  <Select
                    value={registerData.favorite_surface}
                    onValueChange={(value) => setRegisterData({...registerData, favorite_surface: value})}
                  >
                    <SelectTrigger id="register-surface">
                      <SelectValue placeholder="בחר משטח" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="קשה">קשה (Hard Court)</SelectItem>
                      <SelectItem value="חימר">חימר (Clay)</SelectItem>
                      <SelectItem value="דשא">דשא (Grass)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="pt-2 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="register-password">סיסמא *</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="********"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                  <div className="space-y-2 mt-3">
                    <Label htmlFor="register-confirm">אימות סיסמא *</Label>
                    <Input
                      id="register-confirm"
                      type="password"
                      placeholder="********"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                      required
                      data-testid="register-confirm-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'נרשם...' : 'הירשם'}
                  <ArrowRight className="mr-2 w-5 h-5" />
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-blue-600 hover:text-blue-700 font-medium"
                data-testid="toggle-auth-mode-btn"
              >
                {isLogin ? 'אין לך חשבון? הירשם עכשיו' : 'יש לך חשבון? היכנס'}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
